/**
 * Editor Store - Zustand-based state management for the Authoring App
 * Requirements: 3.1, 3.2, 3.3, 3.4, 3.5, 3.6, 6.2, 6.3, 6.5
 */

import { create } from 'zustand';
import { temporal } from 'zundo';
import type {
  CourseBlock,
  Lesson,
  WorkflowState,
} from '@course-authoring/shared-types';

/**
 * Lesson metadata structure (without blockOrder which is managed separately)
 */
export type LessonMeta = Omit<Lesson, 'blockOrder'>;

/**
 * Save status for auto-save functionality
 */
export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

/**
 * Content state - tracked by undo/redo
 * Requirements: 3.1, 3.2
 */
export interface ContentState {
  lessonMeta: LessonMeta;
  blocks: Record<string, CourseBlock>;
  blockOrder: string[];
}

/**
 * Preview progress state - ephemeral, not persisted
 * Requirements: 7.1, 7.2, 7.5
 */
export interface PreviewProgressState {
  /** Block ID -> completion status (ephemeral, not persisted) */
  previewBlockCompletion: Record<string, boolean>;
  /** Current lesson ID in preview mode */
  previewCurrentLocation: string | null;
  /**
   * Whether the learner has engaged (first scroll-intent/interaction) with the
   * previewed lesson. Flips the lesson to `in_progress` independently of block
   * completion. Ephemeral, reset alongside `previewBlockCompletion`.
   */
  previewEngaged: boolean;
}

/**
 * UI state - excluded from undo/redo
 * Requirements: 3.4
 */
export interface UIState {
  activeSelection: string | null;
  isSidebarOpen: boolean;
  lastSaved: Date | null;
  saveStatus: SaveStatus;
  isPreviewMode: boolean;
  previewTargetBlockId: string | null;
  returnTargetBlockId: string | null;
  /**
   * Whether a client (account) context is active. When true, block edits mark
   * the block as an account `override` so the save persists an account-scoped
   * customization (freeze-granularity: only edited/hidden blocks diverge).
   */
  clientContextActive: boolean;
  /**
   * Duplicate block id -> source block id, recorded when a block is
   * duplicated so the next autosave can ask the API to also copy the
   * source's non-English translations onto the new block. Entries are
   * consumed (removed) once the duplicate's temporary id is reconciled to a
   * server-assigned id.
   */
  duplicateSourceByBlockId: Record<string, string>;
}

/**
 * Preview progress actions
 * Requirements: 7.1, 7.2, 7.5
 */
export interface PreviewProgressActions {
  /** Mark a block as complete in preview mode */
  markPreviewBlockComplete: (blockId: string) => void;
  /** Mark the previewed lesson as engaged (first scroll/interaction). */
  markPreviewEngaged: () => void;
  /** Reset preview progress (called on exit preview) */
  resetPreviewProgress: () => void;
  /** Set current location in preview mode */
  setPreviewCurrentLocation: (lessonId: string | null) => void;
}


/**
 * Content mutation actions
 * Requirements: 6.2, 6.3, 6.5, 6.6
 */
export interface ContentActions {
  // Block mutations
  addBlock: (block: CourseBlock, position?: number) => void;
  updateBlock: (id: string, updates: Partial<CourseBlock>) => void;
  removeBlock: (blockId: string) => void;
  reorderBlocks: (newOrder: string[]) => void;

  // Client-context block customization (no-ops on base content; the marker is
  // resolved server-side into an account-scoped BlockContent row).
  /** Hide a base block for the active account (renderState suppress). */
  suppressBlock: (blockId: string) => void;
  /** Re-enable a hidden/edited block: reset to standard (deletes the account row on save). */
  reEnableBlock: (blockId: string) => void;

  // Lesson meta mutations
  updateLessonMeta: (updates: Partial<LessonMeta>) => void;

  // Bulk operations
  setContent: (content: ContentState) => void;

  // Reconcile temporary client-side block ids with server-assigned ids after save.
  reconcileBlockIds: (idMappings: Record<string, string>) => void;
  // Apply a server-normalized block only when the live editor still matches the submitted value.
  reconcileSavedBlock: (blockId: string, submitted: CourseBlock, saved: CourseBlock) => void;
}

/**
 * UI actions - not tracked by undo/redo
 */
export interface UIActions {
  setActiveSelection: (id: string | null) => void;
  toggleSidebar: () => void;
  setSidebarOpen: (isOpen: boolean) => void;
  setLastSaved: (date: Date | null) => void;
  setSaveStatus: (status: SaveStatus) => void;
  setIsPreviewMode: (isPreview: boolean) => void;
  setPreviewTargetBlockId: (id: string | null) => void;
  setReturnTargetBlockId: (id: string | null) => void;
  setClientContextActive: (active: boolean) => void;
  /** Record that `blockId` was duplicated from `sourceBlockId`. */
  recordBlockDuplicateSource: (blockId: string, sourceBlockId: string) => void;
}

/**
 * Complete Editor State
 */
export interface EditorState extends ContentState, UIState, PreviewProgressState, ContentActions, UIActions, PreviewProgressActions {}


/**
 * Default initial state
 */
const defaultContentState: ContentState = {
  lessonMeta: {
    id: '',
    title: '',
    description: '',
    workflowState: 'draft',
    createdAt: '',
    updatedAt: '',
  },
  blocks: {},
  blockOrder: [],
};

const defaultUIState: UIState = {
  activeSelection: null,
  isSidebarOpen: true,
  lastSaved: null,
  saveStatus: 'idle',
  isPreviewMode: false,
  previewTargetBlockId: null,
  returnTargetBlockId: null,
  clientContextActive: false,
  duplicateSourceByBlockId: {},
};

/**
 * Default preview progress state
 * Requirements: 7.5
 */
const defaultPreviewProgressState: PreviewProgressState = {
  previewBlockCompletion: {},
  previewCurrentLocation: null,
  previewEngaged: false,
};

/**
 * Partition function to separate content state from UI state
 * Used by Zundo to exclude UI state from undo/redo history
 * Requirements: 3.4
 */
const partitionState = (state: EditorState): ContentState => ({
  lessonMeta: state.lessonMeta,
  blocks: state.blocks,
  blockOrder: state.blockOrder,
});

/**
 * Editor Store with Zundo temporal middleware
 * Requirements: 3.1, 3.2, 3.3, 3.4, 3.5, 3.6, 6.2, 6.3, 6.5
 */
export const useEditorStore = create<EditorState>()(
  temporal(
    (set) => ({
      // Initial content state
      ...defaultContentState,
      
      // Initial UI state
      ...defaultUIState,

      // Initial preview progress state
      ...defaultPreviewProgressState,

      // Content Actions - Requirements: 6.2, 6.3, 6.5
      
      /**
       * Add a block to the lesson at a specific position
       * Requirements: 6.2
       */
      addBlock: (block: CourseBlock, position?: number) => {
        set((state) => {
          const newBlockOrder = [...state.blockOrder];
          const insertPosition = position !== undefined 
            ? Math.min(Math.max(0, position), newBlockOrder.length)
            : newBlockOrder.length;
          
          newBlockOrder.splice(insertPosition, 0, block.id);

          return {
            blocks: {
              ...state.blocks,
              [block.id]: block,
            },
            blockOrder: newBlockOrder,
          };
        });
      },


      /**
       * Update a block's properties
       */
      updateBlock: (id: string, updates: Partial<CourseBlock>) => {
        set((state) => {
          const block = state.blocks[id];
          if (!block) return state;

          // In client context, editing a block marks it as an account override
          // so the save persists an account-scoped customization. Base context
          // leaves customization markers untouched.
          const clientMarkers = state.clientContextActive
            ? { customizationState: 'override' as const, renderState: 'render' as const }
            : {};

          return {
            blocks: {
              ...state.blocks,
              [id]: {
                ...block,
                ...updates,
                ...clientMarkers,
                updatedAt: new Date().toISOString(),
              } as CourseBlock,
            },
          };
        });
      },

      /**
       * Remove a block from the lesson
       * Requirements: 6.5
       */
      removeBlock: (blockId: string) => {
        set((state) => {
          const newBlockOrder = state.blockOrder.filter((id) => id !== blockId);
          const { [blockId]: removedBlock, ...remainingBlocks } = state.blocks;

          return {
            blocks: remainingBlocks,
            blockOrder: newBlockOrder,
          };
        });
      },

      /**
       * Reorder blocks within the lesson
       * Requirements: 6.3
       */
      reorderBlocks: (newOrder: string[]) => {
        set(() => ({
          blockOrder: newOrder,
        }));
      },

      /**
       * Hide a base block for the active account. Marks the block suppressed;
       * the save resolves this into an account `BlockContent` suppress row.
       */
      suppressBlock: (blockId: string) => {
        set((state) => {
          const block = state.blocks[blockId];
          if (!block) return state;
          return {
            blocks: {
              ...state.blocks,
              [blockId]: {
                ...block,
                customizationState: 'suppressed',
                renderState: 'suppress',
              } as CourseBlock,
            },
          };
        });
      },

      /**
       * Re-enable a hidden/edited block (reset to standard). Marks it inherited;
       * the save deletes the account row so the block resolves to base.
       */
      reEnableBlock: (blockId: string) => {
        set((state) => {
          const block = state.blocks[blockId];
          if (!block) return state;
          return {
            blocks: {
              ...state.blocks,
              [blockId]: {
                ...block,
                customizationState: 'inherited',
                renderState: 'render',
              } as CourseBlock,
            },
          };
        });
      },

      /**
       * Update lesson metadata
       */
      updateLessonMeta: (updates: Partial<LessonMeta>) => {
        set((state) => ({
          lessonMeta: {
            ...state.lessonMeta,
            ...updates,
            updatedAt: new Date().toISOString(),
          },
        }));
      },

      /**
       * Set entire content state (for loading from server)
       */
      setContent: (content: ContentState) => {
        set(() => ({
          ...content,
        }));
      },

      /**
       * After a successful save, swap temporary client-side block ids for the
       * server-assigned ids. Walks blocks/blockOrder and any UI fields keyed by
       * block id. Clears undo history because past states reference the old keys.
       */
      reconcileBlockIds: (idMappings: Record<string, string>) => {
        const mappingEntries = Object.entries(idMappings);
        if (mappingEntries.length === 0) return;

        const apply = (id: string | null | undefined): string | null =>
          id !== undefined && id !== null && id in idMappings
            ? (idMappings[id] as string)
            : (id as string | null);

        set((state) => {
          const nextBlocks: Record<string, CourseBlock> = {};
          for (const [key, block] of Object.entries(state.blocks)) {
            const nextId = key in idMappings ? (idMappings[key] as string) : key;
            nextBlocks[nextId] = { ...block, id: nextId } as CourseBlock;
          }

          const nextOrder = state.blockOrder.map((id) =>
            id in idMappings ? (idMappings[id] as string) : id
          );

          // Duplication bookkeeping is consumed once its temporary id
          // resolves to a server-assigned one — the API call that needed it
          // has already been made.
          const nextDuplicateSourceByBlockId = { ...state.duplicateSourceByBlockId };
          for (const oldId of Object.keys(idMappings)) {
            delete nextDuplicateSourceByBlockId[oldId];
          }

          return {
            blocks: nextBlocks,
            blockOrder: nextOrder,
            activeSelection: apply(state.activeSelection),
            previewTargetBlockId: apply(state.previewTargetBlockId),
            returnTargetBlockId: apply(state.returnTargetBlockId),
            duplicateSourceByBlockId: nextDuplicateSourceByBlockId,
          };
        });

        useEditorStore.temporal.getState().clear();
      },

      reconcileSavedBlock: (blockId: string, submitted: CourseBlock, saved: CourseBlock) => {
        set((state) => {
          const current = state.blocks[blockId];
          if (!current || JSON.stringify(current) !== JSON.stringify(submitted)) return state;
          return { blocks: { ...state.blocks, [blockId]: saved } };
        });
      },

      // UI Actions - Not tracked by undo/redo

      /**
       * Set the currently selected block
       */
      setActiveSelection: (id: string | null) => {
        set(() => ({
          activeSelection: id,
        }));
      },

      /**
       * Toggle sidebar visibility
       */
      toggleSidebar: () => {
        set((state) => ({
          isSidebarOpen: !state.isSidebarOpen,
        }));
      },

      /**
       * Set sidebar visibility
       */
      setSidebarOpen: (isOpen: boolean) => {
        set(() => ({
          isSidebarOpen: isOpen,
        }));
      },

      /**
       * Set last saved timestamp
       */
      setLastSaved: (date: Date | null) => {
        set(() => ({
          lastSaved: date,
        }));
      },

      /**
       * Set save status
       */
      setSaveStatus: (status: SaveStatus) => {
        set(() => ({
          saveStatus: status,
        }));
      },

      setIsPreviewMode: (isPreview: boolean) => {
        set(() => ({
          isPreviewMode: isPreview,
        }));
      },

      setPreviewTargetBlockId: (id: string | null) => {
        set(() => ({
          previewTargetBlockId: id,
        }));
      },

      setReturnTargetBlockId: (id: string | null) => {
        set(() => ({
          returnTargetBlockId: id,
        }));
      },

      setClientContextActive: (active: boolean) => {
        set(() => ({
          clientContextActive: active,
        }));
      },

      recordBlockDuplicateSource: (blockId: string, sourceBlockId: string) => {
        set((state) => ({
          duplicateSourceByBlockId: {
            ...state.duplicateSourceByBlockId,
            [blockId]: sourceBlockId,
          },
        }));
      },

      // Preview Progress Actions - Requirements: 7.1, 7.2, 7.5

      /**
       * Mark a block as complete in preview mode
       * Requirements: 7.2
       */
      markPreviewBlockComplete: (blockId: string) => {
        set((state) => ({
          previewBlockCompletion: {
            ...state.previewBlockCompletion,
            [blockId]: true,
          },
        }));
      },

      /**
       * Mark the previewed lesson as engaged (first scroll/interaction).
       * Idempotent — flips the lesson to in_progress in the preview sidebar.
       */
      markPreviewEngaged: () => {
        set((state) => (state.previewEngaged ? {} : { previewEngaged: true }));
      },

      /**
       * Reset preview progress (called on exit preview)
       * Requirements: 7.5
       */
      resetPreviewProgress: () => {
        set(() => ({
          ...defaultPreviewProgressState,
        }));
      },

      /**
       * Set current location in preview mode
       * Requirements: 7.1
       */
      setPreviewCurrentLocation: (lessonId: string | null) => {
        set(() => ({
          previewCurrentLocation: lessonId,
        }));
      },
    }),
    {
      // Zundo configuration - Requirements: 3.3, 3.4
      // Only track content state changes, exclude UI state
      partialize: partitionState,
      // Limit history size to prevent memory issues
      limit: 100,
    }
  )
);


/**
 * Selector for content state only
 */
export const selectContentState = (state: EditorState): ContentState => ({
  lessonMeta: state.lessonMeta,
  blocks: state.blocks,
  blockOrder: state.blockOrder,
});

let cachedAllBlocksBlockOrderRef: string[] | null = null;
let cachedAllBlocksBlocksRef: Record<string, CourseBlock> | null = null;
let cachedAllBlocksResult: CourseBlock[] = [];

/**
 * Get all blocks in order
 */
export const selectAllBlocks = (state: EditorState): CourseBlock[] => {
  // Important: this selector is used with Zustand's `useSyncExternalStore` integration.
  // `getSnapshot()` must return a cached (referentially stable) value when the store
  // hasn't changed, otherwise React can warn and/or enter a render loop.
  if (state.blockOrder === cachedAllBlocksBlockOrderRef && state.blocks === cachedAllBlocksBlocksRef) {
    return cachedAllBlocksResult;
  }

  const next = state.blockOrder
    .map((blockId) => state.blocks[blockId])
    .filter((block): block is CourseBlock => block !== undefined);

  cachedAllBlocksBlockOrderRef = state.blockOrder;
  cachedAllBlocksBlocksRef = state.blocks;
  cachedAllBlocksResult = next;

  return next;
};
